import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Heart, Send, Trash2, ArrowLeft } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Floating } from "../components/Floating";
import { AuthDialog } from "../components/AuthDialog";
import { SavedSearchesPanel } from "../components/SavedSearches";
import { StatusBadge, GoldButton, EASE } from "../components/shared";
import { PROPERTIES } from "../data/site";
import { useAuth } from "../context/AuthContext";
import { useLenis } from "../hooks/useLenis";
import { useLiveListings } from "../hooks/useLiveListings";

export default function Shortlist() {
  const { user, loading, shortlist, toggleShortlist, sendShortlist, setAuthOpen } = useAuth();
  const navigate = useNavigate();
  const [phone, setPhone] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useLenis();
  const live = useLiveListings();

  useEffect(() => {
    document.title = "My Shortlist | Radiance Realtors";
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!loading && !user) {
      sessionStorage.setItem("afterLogin", "/shortlist");
      setAuthOpen(true);
      navigate("/", { replace: true });
    }
  }, [loading, user, navigate, setAuthOpen]);

  const items = [...live, ...PROPERTIES].filter((p) => shortlist.includes(p.id));

  const send = async (e) => {
    e.preventDefault();
    if (!phone.trim()) return toast.error("Please share a phone number we can reach you on.");
    setBusy(true);
    try {
      await sendShortlist({ phone, message: note, items: items.map(({ id, project, config, price }) => ({ id, project, config, price })) });
      toast.success("Shortlist sent. An advisor will call you with pricing and availability.");
      setNote("");
      setPhone("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send right now. Please try WhatsApp or call us.");
    } finally {
      setBusy(false);
    }
  };

  if (loading || !user) {
    return <div className="min-h-screen bg-[#0A0A0A] grid place-items-center text-[11px] uppercase tracking-[0.3em] text-[#D4AF37]" data-testid="shortlist-loading">Loading&hellip;</div>;
  }

  return (
    <div className="grain" data-testid="shortlist-page">
      <Navbar onAdvisor={() => navigate("/#contact")} />
      <main className="min-h-screen bg-[#0A0A0A] pt-32 pb-28">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Link to="/#inventory" data-testid="shortlist-back-link" className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-[#71717A] hover:text-[#F3E5AB] transition-colors mb-8"><ArrowLeft size={12} /> Back to Inventory</Link>
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] mb-3">Welcome, {user.name.split(" ")[0]}</p>
          <h1 className="font-serif-lux text-4xl sm:text-5xl lg:text-6xl text-[#F5F5F0] mb-3" data-testid="shortlist-title">
            My <span className="text-gold-gradient italic">Shortlist.</span>
          </h1>
          <p className="text-sm text-[#71717A] mb-12" data-testid="shortlist-count">{items.length} {items.length === 1 ? "property" : "properties"} saved</p>

          {items.length === 0 ? (
            <div className="border border-[#D4AF37]/20 bg-[#0D0D0D] rounded-[2px] p-12 text-center" data-testid="shortlist-empty">
              <Heart size={28} className="mx-auto text-[#D4AF37]/60 mb-4" />
              <p className="font-serif-lux text-2xl text-[#F5F5F0] mb-2">Nothing saved yet.</p>
              <p className="text-sm text-[#71717A] mb-8">Tap the heart on any listing to keep it here.</p>
              <GoldButton data-testid="shortlist-browse-btn" onClick={() => navigate("/#inventory")}>Browse Inventory</GoldButton>
            </div>
          ) : (
            <div className="grid lg:grid-cols-[1fr_380px] gap-10 items-start">
              <div className="grid sm:grid-cols-2 gap-5" data-testid="shortlist-grid">
                {items.map((p, i) => (
                  <motion.article key={p.id} data-testid={`shortlist-card-${p.id}`} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: i * 0.06, ease: EASE }} className="card-lux rounded-[2px] overflow-hidden">
                    <div className="relative h-44 overflow-hidden">
                      <img src={p.image} alt={p.project} className="w-full h-full object-cover img-cinematic" />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#101010] via-transparent to-transparent" />
                      <div className="absolute top-3 left-3"><StatusBadge status={p.status} /></div>
                    </div>
                    <div className="p-5">
                      <p className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] mb-1.5">{p.category} &bull; {p.config}</p>
                      <h3 className="font-serif-lux text-xl text-[#F5F5F0] mb-2">{p.project}</h3>
                      <div className="flex items-baseline justify-between mb-4">
                        <span className="text-base font-semibold text-[#F3E5AB]">{p.price}</span>
                        <span className="text-[11px] text-[#71717A]">{p.size}</span>
                      </div>
                      <button data-testid={`shortlist-remove-btn-${p.id}`} onClick={() => toggleShortlist(p)} className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#71717A] hover:text-[#F3E5AB] transition-colors">
                        <Trash2 size={12} /> Remove
                      </button>
                    </div>
                  </motion.article>
                ))}
              </div>

              <form onSubmit={send} className="lg:sticky lg:top-28 border border-[#D4AF37]/25 bg-[#D4AF37]/[0.04] rounded-[2px] p-7" data-testid="shortlist-send-form">
                <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] mb-2">Next Step</p>
                <p className="font-serif-lux text-2xl text-[#F5F5F0] mb-2">Send my shortlist to an advisor</p>
                <p className="text-[12px] text-[#71717A] leading-relaxed mb-6">We reply with live pricing, availability and a suggested site-visit plan for {items.length === 1 ? "this property" : `these ${items.length} properties`}.</p>
                <input data-testid="shortlist-phone-input" className="lux-input mb-3" placeholder="Phone / WhatsApp *" value={phone} onChange={(e) => setPhone(e.target.value)} />
                <textarea data-testid="shortlist-note-input" className="lux-input mb-5" rows={3} placeholder="Anything specific? (budget, timeline, floor preference)" value={note} onChange={(e) => setNote(e.target.value)} />
                <GoldButton data-testid="shortlist-send-btn" disabled={busy} className="w-full"><Send size={14} /> {busy ? "Sending..." : "Send to Advisor"}</GoldButton>
              </form>
            </div>
          )}

          <SavedSearchesPanel />
        </div>
      </main>
      <Footer />
      <Floating />
      <AuthDialog />
    </div>
  );
}
